import {
  Box,
  RadioGroup as ChakraRadioGroup,
  HStack,
  Span,
  Wrap,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { labelStyles } from './label';
import { InfoTip, Alert } from '.';

export interface RadioProps extends ChakraRadioGroup.ItemProps {
  rootRef?: React.RefObject<HTMLDivElement | null>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  function Radio(props, ref) {
    const { children, inputProps, rootRef, ...rest } = props;
    return (
      <ChakraRadioGroup.Item ref={rootRef} {...rest}>
        <ChakraRadioGroup.ItemHiddenInput ref={ref} {...inputProps} />
        <ChakraRadioGroup.ItemIndicator />
        {children && (
          <ChakraRadioGroup.ItemText>{children}</ChakraRadioGroup.ItemText>
        )}
      </ChakraRadioGroup.Item>
    );
  },
);

export interface RadioGroupProps {
  label?: string;
  infoTip?: string;
  items: any[];
  keyForDisplay?: string | null;
  keyForValue?: string | null;
  getEntireItem?: boolean;
  defaultValue?: string | number | null;
  onSelect: (value: any) => void;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  children?: React.ReactNode;
  childrenProps?: React.HTMLAttributes<HTMLDivElement>;
  disabled?: boolean;
  disableBorder?: boolean;
  [key: string]: any;
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  function RadioGroup(props, ref) {
    const {
      items,
      keyForDisplay,
      keyForValue,
      getEntireItem = false,
      defaultValue = null,
      onSelect,
      label,
      infoTip,
      containerProps,
      children,
      childrenProps,
      disabled,
      disableBorder = false,
      ...rest
    } = props;

    const [value, setValue] = React.useState<string | null>(
      defaultValue ? String(defaultValue) : null,
    );

    const typeOfValue = defaultValue
      ? typeof defaultValue
      : items?.[0]?.[keyForValue]
        ? typeof items?.[0]?.[keyForValue]
        : 'string';

    return (
      <Box position="relative">
        <Box
          border={disableBorder ? 'none' : '1px solid'}
          borderColor="border"
          borderRadius="sm"
          p={children ? 1.5 : 0.5}
          _focusWithin={{
            borderColor: 'primary.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
          }}
          transition="all 0.2s"
        >
          <ChakraRadioGroup.Root
            ref={ref}
            defaultValue={defaultValue ? String(defaultValue) : null}
            value={value}
            onValueChange={(e) => {
              setValue(e.value);
              onSelect(
                getEntireItem
                  ? {
                      value:
                        typeOfValue === 'number' ? Number(e.value) : e.value,
                      item: items.find(
                        (item: any) => item[keyForValue!] === e.value,
                      ),
                    }
                  : typeOfValue === 'number'
                    ? Number(e.value)
                    : e.value,
              );
            }}
            disabled={disabled}
            {...rest}
          >
            {label && (
              <Text {...(labelStyles.floating({}) as any)} disabled={disabled}>
                {label}
                {infoTip && (
                  <Span pointerEvents="auto">
                    <InfoTip>
                      <Alert>{infoTip}</Alert>
                    </InfoTip>
                  </Span>
                )}
              </Text>
            )}
            <Wrap
              gap="4"
              {...(children && {
                alignItems: 'center',
                justifyContent: 'space-around',
                p: 5,
              })}
              {...containerProps}
            >
              {items.map((item: any) => (
                <ChakraRadioGroup.Item
                  key={String(item[keyForValue!])}
                  value={String(item[keyForValue!])}
                >
                  <ChakraRadioGroup.ItemHiddenInput />
                  <ChakraRadioGroup.ItemIndicator />
                  <ChakraRadioGroup.ItemText>
                    {String(item[keyForDisplay!])}
                  </ChakraRadioGroup.ItemText>
                </ChakraRadioGroup.Item>
              ))}
            </Wrap>
          </ChakraRadioGroup.Root>

          {children && (
            <Box
              display="flex"
              flex={1}
              flexDirection="column"
              width="100%"
              minWidth={0}
              whiteSpace="normal"
              p={5}
              height="100%"
              overflow="auto"
              gap={4}
              {...childrenProps}
            >
              {children}
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);
